import {
  FluidAnchorPosition,
  FluidIconConfig,
  FluidIconKey,
  FluidTheme,
  FluidTreeMenuConfig,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../../../utils/themes/theme.variables';
import { FluidSidebar } from '../fluid-sidebar';

interface SidebarProps extends FluidFunctionalComponentProps {
  host: FluidSidebar;
  treeMenuConfig?: FluidTreeMenuConfig;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Sidebar Content
 */
export const SidebarContent: FunctionalComponent<SidebarProps> = (
  props: SidebarProps
) => {
  const { host } = props;
  const hideContent = !host.visible && !host._transitioning;

  const handleTransitionEnd = () => {
    host._transitioning = false;
    // If Sidebar is open & Tree Menu has "auto-scroll" enabled, scroll selected item into view
    host.visible &&
      host._treeMenuRef?.autoScrollSelectedItem &&
      host._treeMenuRef?.scrollSelectedItemIntoView();
  };

  return (
    <div
      onAnimationEnd={() => handleTransitionEnd()}
      style={hideContent && { display: 'none' }}
      class={{
        'sidebar-content': true,
        'content-visible': host.visible,
        'content-hidden': !host.visible,
        'transition-state': host._transitioning,
      }}>
      <SidebarTreeMenu {...props} />
      <slot />
    </div>
  );
};

/**
 * Sidebar Toggle Container + Button
 * NOTE: styles for button's "part" attribute located in FLUID Page.
 */
export type SidebarToggleProps = {
  ariaLabel: string;
  anchor: FluidAnchorPosition.LEFT | FluidAnchorPosition.RIGHT;
  collapsible: boolean;
  theme: FluidTheme;
  visible: boolean;
  mobileViewport: boolean;
  offsetTop: number;
  transitioning: boolean;
  onClickHandler: () => void;
};
export const SidebarToggle: FunctionalComponent<SidebarToggleProps> = (
  props: SidebarToggleProps
) => {
  const {
    ariaLabel,
    anchor,
    collapsible,
    theme,
    visible,
    mobileViewport,
    offsetTop,
    transitioning,
    onClickHandler,
  } = props;

  if (!collapsible && !mobileViewport) return;

  return (
    <div
      class={{
        [`sidebar-toggle-container toggle-${anchor}`]: true,
        'expand-toggle-container': !visible,
      }}
      style={
        !!offsetTop && {
          'max-height': `calc(100% - ${offsetTop}px)`,
          top: `${offsetTop}px`,
        }
      }>
      <button
        title={ariaLabel}
        aria-label={ariaLabel}
        aria-expanded={String(visible)}
        onClick={() => onClickHandler()}
        part={!visible ? 'sidebar-expand-toggle' : null}
        class={{
          [`sidebar-toggle-${anchor}`]: true,
          'expand-toggle': !visible,
          'transition-state': transitioning,
        }}
        style={
          !!offsetTop && {
            'max-height': `calc(100vh - ${offsetTop}px)`,
            ...(!visible && { top: `${offsetTop}px` }),
          }
        }>
        <fluid-icon
          class='toggle-icon'
          config={getToggleIconConfig(anchor, visible, theme)}
        />
      </button>
    </div>
  );
};

// ================================================================ //
// -- Private Functional Components

/**
 * Sidebar Tree Menu
 */
const SidebarTreeMenu: FunctionalComponent<SidebarProps> = (
  props: SidebarProps
) => {
  const { host, treeMenuConfig } = props;

  return (
    !!treeMenuConfig && (
      <fluid-tree-menu
        ref={(el: HTMLFluidTreeMenuElement) => (host._treeMenuRef = el)}
        displayDensity={host.displayDensity}
        config={{
          ...treeMenuConfig,
          sidebarMenu: true,
          theme: host.theme,
          themePalette: host.themePalette,
          overrideGlobalTheme: true,
        }}
      />
    )
  );
};

// ================================================================ //
// -- Functional Component Utilities (Private)

/**
 * Returns FluidIconConfig object for Sidebar's toggle button. Icon "key"
 * determined by Sidebar's anchor position & "visible" status.
 * @param anchor - Sidebar's "anchor" Prop value
 * @param visible - Sidebar's "visible" Prop value
 * @param theme - Sidebar's "theme" Prop value
 */
const getToggleIconConfig = (
  anchor: FluidAnchorPosition.LEFT | FluidAnchorPosition.RIGHT,
  visible: boolean,
  theme: FluidTheme
): FluidIconConfig => {
  let iconKey;

  if (anchor === FluidAnchorPosition.LEFT) {
    iconKey = visible ? FluidIconKey.COLLAPSE_LEFT : FluidIconKey.EXPAND_RIGHT;
  } else {
    iconKey = visible ? FluidIconKey.COLLAPSE_RIGHT : FluidIconKey.EXPAND_LEFT;
  }

  return {
    key: iconKey,
    theme: theme,
    overrideGlobalTheme: true,
    ...themeVar<FluidIconConfig>(theme, 'icons.sidebar.toggle'),
  };
};
